import React, { FC, ReactElement, useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import * as topojson from 'topojson-client'
import styled from 'styled-components'

import ZoomOutIcon from './Assets/ZoomOut'
import ZoomInIcon from './Assets/ZoomIn'
import FullScreenIcon from './Assets/FullScreen'
import ExitFullScreenIcon from './Assets/ExitFullScreen'
import { useIntersectionObserver } from 'usehooks-ts'

const MINIMUM_NUMBER_OF_SAMPLES = 50
const clamp = (num: number, min: number, max: number) =>
  Math.min(Math.max(num, min), max)
const INITIAL_HEIGHT = 680
const INITIAL_WIDTH = 975
const MAX_ZOOM = 5
const EXTENT_PADDING = 100
export interface Props {
  propertyNameToCompare: string
  indexOfPropertyToCompare?: number
  municipalityData: { bfsId: number; [key: string]: any }[]
  minValue: number
  maxValue: number
  year: number
  topoJson: any
  activeMunicipalityId?: number
  newActiveMunicipalityIdHandler: (newId: number) => void
  hoverHandler: (newData: any) => void
  isZoomable?: boolean
  zoomIntoActiveMunicipality: boolean
  zoomDistance: number
  disableTouchOnMobile?: boolean
  colorPalette?: [string, string, string]
  activeStrokeWidth: string
  activeStrokeColor: string
  municipalityStrokeColor: string
  municipalityStrokeWidth: string
  municipalityFallbackFillColor: string
  hoverStrokeWidth: string
  hoverStrokeColor: string
  lakeFillColor: string
  lakeStrokeColor: string
  lakeStrokeWidth: string
  nationalBorderColor: string
  nationalBorderWidth: string
  cantonStrokeColor: string
  cantonStrokeWidth: string
  toolTipRef: React.RefObject<HTMLDivElement>
  fullscreenChangeHandler?: (isFullscreen: boolean) => void
  isFullscreen?: boolean
  fullscreenHint?: string
  scrollIntoView?: boolean
}
const ChoroplethMap: FC<Props> = ({
  municipalityData,
  propertyNameToCompare,
  indexOfPropertyToCompare,
  minValue,
  maxValue,
  year,
  topoJson,
  activeMunicipalityId,
  newActiveMunicipalityIdHandler,
  hoverHandler,
  isZoomable,
  zoomIntoActiveMunicipality,
  zoomDistance,
  disableTouchOnMobile,
  colorPalette,
  activeStrokeWidth,
  activeStrokeColor,
  municipalityStrokeColor,
  municipalityStrokeWidth,
  municipalityFallbackFillColor,
  hoverStrokeWidth,
  hoverStrokeColor,
  lakeFillColor,
  lakeStrokeColor,
  lakeStrokeWidth,
  nationalBorderColor,
  nationalBorderWidth,
  cantonStrokeColor,
  cantonStrokeWidth,
  toolTipRef,
  fullscreenChangeHandler,
  isFullscreen,
  fullscreenHint,
  scrollIntoView,
}): ReactElement => {
  const [chTopoJSON, setChTopoJSON] = useState<any | null>()
  const [path, setPath] = useState<d3.GeoPath | null>()
  const [geoJsonMuni, setGeoJsonMuni] = useState<any | null>()
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [height, setHeight] = useState(INITIAL_HEIGHT)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [width, setWidth] = useState(INITIAL_WIDTH)
  const [activeMunicipalitySelection, setActiveMunicipalitySelection] =
    useState<d3.Selection<d3.BaseType, unknown, null, undefined> | null>(null)
  const [isInApp, setIsInApp] = useState(false)
  const mapControlsRef = useRef<HTMLDivElement | null>(null)
  const entry = useIntersectionObserver(mapControlsRef, {})
  const controlsAreVisible = !!entry?.isIntersecting

  // Refs
  const countryBorderRef = useRef<SVGPathElement | null>(null)
  const municipalityLayerRef = useRef<SVGPathElement | null>(null)
  const highlightLayerRef = useRef<SVGPathElement | null>(null)
  const swissChoroplethMapRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)

  const tooltipDistanceToCursor = 10
  const color = d3
    .scaleLinear<string>()
    .domain([minValue, (minValue + maxValue) / 2, maxValue])
    .range(colorPalette || ['green', 'lightgrey', 'red'])

  const zoomed = (event: any) => {
    const { transform } = event
    setZoomLevel(transform.k)
    setIsZoomedIn(transform.k > 1)
    event.sourceEvent?.stopImmediatePropagation()
    d3.select(svgRef.current).selectChildren().attr('transform', transform)
  }

  const zoom = d3
    .zoom()
    .filter((event) => {
      const isTouchScrolling =
        event?.type === 'touchstart' &&
        event?.touches.length === 1 &&
        zoomLevel <= 1

      return (
        !(!event?.ctrlKey && event?.type === 'wheel' && !isFullscreen) &&
        !isTouchScrolling
      )
    })
    .scaleExtent([1, MAX_ZOOM + 3])
    .translateExtent([
      [0 - EXTENT_PADDING, 0 - EXTENT_PADDING],
      [INITIAL_WIDTH + EXTENT_PADDING, INITIAL_HEIGHT + EXTENT_PADDING],
    ])
    .on('zoom', zoomed)
  d3.select(svgRef.current).call(zoom as any)
  const setTopoJson = (newTopoJson: any) => {
    console.log({ newTopoJson })

    setGeoJsonMuni(
      topojson.feature(newTopoJson, newTopoJson.objects.municipalities)
    )
    setChTopoJSON(newTopoJson)
    const projection = d3
      .geoMercator()
      .rotate([0, 0])
      .center([8.23, 46.885])
      .scale(11980)
      .translate([width / 2, height / 2])
      .precision(0.1)
    setPath(() => d3.geoPath(projection))
  }
  function getValueToCompare(bfsIdToFind: number): number | string | undefined {
    const foundInMunicipaltyData = municipalityData.find(
      (muni) => +muni.bfsId === +bfsIdToFind
    )
    if (foundInMunicipaltyData) {
      if (typeof indexOfPropertyToCompare !== 'undefined') {
        const foundValue =
          foundInMunicipaltyData[propertyNameToCompare][
            indexOfPropertyToCompare
          ]
        const lastValue =
          foundInMunicipaltyData[propertyNameToCompare]?.[
            foundInMunicipaltyData[propertyNameToCompare].length - 1
          ]
        if (isNaN(foundValue) || lastValue === 0) {
          return undefined
        }
        return (
          foundInMunicipaltyData[propertyNameToCompare][
            indexOfPropertyToCompare
          ] /
          foundInMunicipaltyData[propertyNameToCompare]?.[
            foundInMunicipaltyData[propertyNameToCompare].length - 1
          ]
        )
      }
      return foundInMunicipaltyData[propertyNameToCompare]
    }
    return undefined
  }
  function repositionToolTip(mouseX: number, mouseY: number) {
    const toolTipElement = d3.select(toolTipRef.current).node() as HTMLElement
    const widthOfTooltip = toolTipElement?.clientWidth
    const windowWidth = document.documentElement.clientWidth
    const toolTipShouldBeLeft =
      widthOfTooltip + mouseX + tooltipDistanceToCursor > windowWidth
    if (!toolTipShouldBeLeft) {
      d3.select(toolTipRef.current)
        .style('left', mouseX + tooltipDistanceToCursor + 'px')
        .style('top', mouseY + tooltipDistanceToCursor + 'px')
    } else {
      d3.select(toolTipRef.current)
        .style('left', mouseX - widthOfTooltip - tooltipDistanceToCursor + 'px')
        .style('top', mouseY + tooltipDistanceToCursor + 'px')
    }
  }
  function addHoverInteractions() {
    const mapElement = d3
      .select(swissChoroplethMapRef.current)
      .node() as HTMLElement

    if (
      mapElement?.clientWidth > 600 &&
      d3.select(municipalityLayerRef.current).selectChildren().nodes().length >
        0
    ) {
      console.log('add hover interactions')

      d3.select(municipalityLayerRef.current)
        .selectChildren()
        .on('mouseover', function (this: any, event: MouseEvent) {
          d3.select(toolTipRef.current).style('opacity', 1)
          repositionToolTip(event.clientX, event.clientY)
          hoverHandler(
            municipalityData.find(
              (muni) => +muni.bfsId === +d3.select(this).attr('bfsId')
            )
          )
          // put hovered municipality on top
          d3.select(this).raise()
          // put active municipality over the hovered one
          d3.select(municipalityLayerRef.current)
            .selectChild('.active-municipality')
            .raise()
        })
        .on('mouseout', function () {
          d3.select(toolTipRef.current).style('opacity', 0)
        })
        .on('mousemove', function (event: MouseEvent) {
          repositionToolTip(event.clientX, event.clientY)
        })
      window.addEventListener(
        'scroll',
        function () {
          d3.select(toolTipRef.current).style('opacity', 0)
        },
        false
      )
    }
  }
  function zoomTo(
    selection: d3.Selection<d3.BaseType, unknown, null, undefined>
  ) {
    const d = geoJsonMuni.features.find(
      (muni: any) => +muni.id === +selection.attr('bfsId')
    )
    const [[x0, y0], [x1, y1]] = path!.bounds(d)

    d3.select(svgRef.current)
      .transition()
      .duration(750)
      .call(
        zoom.transform as any,
        d3.zoomIdentity
          .translate(INITIAL_WIDTH / 2, INITIAL_HEIGHT / 2)
          .scale(
            Math.min(
              MAX_ZOOM,
              0.9 /
                Math.max((x1 - x0) / INITIAL_WIDTH, (y1 - y0) / INITIAL_HEIGHT)
            )
          )
          .translate(-(x0 + x1) / 2, -(y0 + y1) / 2)
      )
    setIsZoomedIn(true)
  }
  function zoomInToWhereIam() {
    const svgSelection = d3.select(svgRef.current)
    if (!svgSelection || !zoom) return
    svgSelection.transition().call(zoom.scaleBy as any, 2)
  }
  function zoomOut() {
    const svgSelection = d3.select(svgRef.current)
    if (path && svgSelection) {
      svgSelection
        .transition()
        .duration(750)
        .call(
          zoom.transform as any,
          d3.zoomIdentity,
          d3.zoomTransform(svgSelection.node()!).invert([width / 2, height / 2])
        )
      setIsZoomedIn(false)
    }
  }
  function zoomToActiveMunicipality(passedActiveMunicipalityId?: number) {
    const bfsIdToZoomTo = passedActiveMunicipalityId || activeMunicipalityId
    if (bfsIdToZoomTo) {
      activeMunicipalitySelection && zoomTo(activeMunicipalitySelection!)
    }
  }
  function scrollToTargetAdjusted(target: Element) {
    var elementPosition = target.getBoundingClientRect().top
    var offsetPosition = elementPosition + window.pageYOffset - headerHeight

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })
  }
  function highlightActiveMunicipality() {
    console.log('highlightActiveMunicipality', activeMunicipalityId)
    activeMunicipalitySelection?.attr('class', 'municipality')
    const muniToHighlight = d3
      .select(municipalityLayerRef.current)
      .selectChild('#bfsId-' + activeMunicipalityId)
      ?.attr('class', 'active-municipality')
    if (activeMunicipalityId && muniToHighlight.node()) {
      muniToHighlight.raise()
      setActiveMunicipalitySelection(muniToHighlight)
      isZoomedIn && zoomTo(muniToHighlight)
    }
  }
  function addMunicipalityPaths() {
    if (path) {
      d3.select(municipalityLayerRef.current).selectAll('*').remove()

      console.log('geoJsonMuni.features', geoJsonMuni.features.length)
      d3.select(municipalityLayerRef.current)
        .selectAll('*')
        .data(geoJsonMuni.features)
        .enter()
        .append('path')
        .attr('d', (feature: any) => {
          return path(feature)
        })
        .attr('class', 'municipality')
        .attr('stroke', municipalityStrokeColor)
        // .attr('stroke-width', municipalityStrokeWidth)
        .attr('id', (feature: any) => 'bfsId-' + feature.id)
        .attr('bfsId', (feature: any) => feature.id)
        .on('click', function (this: any, event: PointerEvent, feature: any) {
          console.log('click', { event, feature })
          // this?.parentNode?.appendChild(this)
          d3.select(this).raise()
          // d3.select(this)?.raise()
          if (window.innerWidth > 600 || !disableTouchOnMobile) {
            newActiveMunicipalityIdHandler(feature.id ?? feature.properties.id)
          }
        })
      colorMunicipalities()
      addHoverInteractions()
    }
  }
  function fullscreenButtonPressed() {
    console.log('window.innerHeight', window.innerHeight)
    setHeight(window.innerHeight)
    setWidth(swissChoroplethMapRef.current?.clientWidth ?? 0)
    fullscreenChangeHandler?.(!isFullscreen)
    setTimeout(() => {
      // zoomOut()
      svgRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }, 100)
  }
  useEffect(() => {
    // if we already have the (local) topojson, we don't need to fetch it
    if (topoJson) {
      console.log('unsing topoJson passed as prop')
      setTopoJson(topoJson)
      return
    }
    fetch('https://unpkg.com/swiss-maps/' + year + '/ch-combined.json').then(
      (res) =>
        res.json().then((parsed) => {
          console.log('using topoJson fetched year: ', year)
          setTopoJson(parsed)
        })
    )
    const userAgent = window.navigator.userAgent.toLowerCase()
    if (/iphone|ipod|ipad/.test(userAgent)) {
      // iOS
      if (!/safari/.test(userAgent)) {
        // iOS webView
        setIsInApp(true)
      }
    } else {
      if (userAgent.includes('wv')) {
        // Android webView
        setIsInApp(true)
      }
    }
  }, [topoJson])
  useEffect(() => {
    if (path) {
      addMunicipalityPaths()
    }
    activeMunicipalityId &&
      zoomIntoActiveMunicipality &&
      zoomToActiveMunicipality()

    console.log('useEffect: [geoJsonMuni, path]')
  }, [geoJsonMuni, path])
  useEffect(() => {
    zoomIntoActiveMunicipality ? zoomToActiveMunicipality() : zoomOut()
  }, [zoomIntoActiveMunicipality])

  const colorMunicipalities = () => {
    d3.select(municipalityLayerRef.current)
      .selectAll('path')
      .attr('fill', (feature: any) => {
        const foundMuni = municipalityData.find(
          (muni) => +muni.bfsId === +feature.id
        )
        const valueToCompare = getValueToCompare(feature.id)
        let newColor
        if (valueToCompare !== undefined && valueToCompare !== "" && isNaN(+valueToCompare) === false) {
          newColor = d3.color(color(clamp(+valueToCompare, minValue, maxValue)))
        } else {
          newColor = d3.color(municipalityFallbackFillColor)
        }
        // newColor!.opacity = foundMuni?.Tot < MINIMUM_NUMBER_OF_SAMPLES ? 0.7 : 1
        return newColor!.toString()
      })
    highlightActiveMunicipality()
  }
  useEffect(() => {
    if (activeMunicipalitySelection && zoomIntoActiveMunicipality) {
      zoomToActiveMunicipality()
    }
  }, [activeMunicipalitySelection])
  useEffect(() => {
    highlightActiveMunicipality()
    scrollIntoView &&
      activeMunicipalityId &&
      swissChoroplethMapRef.current &&
      scrollToTargetAdjusted(swissChoroplethMapRef.current)
    console.log('useEffect: [activeMunicipalityId]')
  }, [activeMunicipalityId])

  useEffect(() => {
    colorMunicipalities()
    console.log('useEffect: [municipalityData]')
  }, [municipalityData, indexOfPropertyToCompare, propertyNameToCompare])
  useEffect(() => {
    const headerElement = document.querySelector('[class*=FullHeader_root]')

    let headerHeight = 0
    if (headerElement) {
      // check if header is sticky
      const headerStyle = window.getComputedStyle(headerElement)
      const isSticky =
        headerStyle.position === 'sticky' && headerStyle.top === '0px'

      if (isSticky) {
        headerHeight = headerElement.clientHeight
        setHeaderHeight(headerHeight)
      }
    }
  }, [])
  return (
    <div>
      {/* {window.navigator.userAgent.toLowerCase()} */}
      <div id='swiss-choropleth-map' ref={swissChoroplethMapRef}>
        {chTopoJSON && path && municipalityData && (
          <svg
            style={{
              height: isFullscreen ? height : undefined,
              width: isFullscreen ? width : undefined,
            }}
            viewBox={'0,0,' + INITIAL_WIDTH + ',' + INITIAL_HEIGHT}
            ref={svgRef}
          >
            <defs>
              <pattern
                id='diagonalHatch'
                x='0'
                y='0'
                width='8'
                height='8'
                patternUnits='userSpaceOnUse'
              >
                <path
                  d='M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2'
                  stroke='#b5b5b5'
                  strokeWidth='2'
                />
              </pattern>
            </defs>
            <g id='group'>
              <g
                fill='none'
                stroke='#000'
                strokeLinejoin='round'
                strokeLinecap='round'
              >
                {/* {chTopoJSON.objects.cantons && (
                  <path
                    strokeWidth={cantonStrokeWidth}
                    stroke={cantonStrokeColor}
                    d={
                      path(
                        topojson.mesh(
                          chTopoJSON,
                          chTopoJSON.objects.cantons,
                          (a, b) => a !== b
                        )
                      ) as string
                    }
                  />
                )}
                {chTopoJSON.objects.country && (
                  <path
                    strokeWidth={nationalBorderWidth}
                    id='country-border'
                    ref={countryBorderRef}
                    stroke={nationalBorderColor}
                    d={
                      path(
                        topojson.feature(chTopoJSON, chTopoJSON.objects.country)
                      ) as string
                    }
                  />
                )} */}
                {chTopoJSON.objects.lakes && (
                  <path
                    fill={lakeFillColor}
                    stroke={lakeStrokeColor}
                    strokeWidth={lakeStrokeWidth}
                    d={
                      path(
                        topojson.feature(chTopoJSON, chTopoJSON.objects.lakes)
                      ) as string
                    }
                  />
                )}
              </g>
              <g
                id='municipality-layer'
                ref={municipalityLayerRef}
                strokeLinejoin='round'
                strokeWidth={+municipalityStrokeWidth / (zoomLevel * 0.8)}
              />
            </g>
          </svg>
        )}
        <MapControls
          id='map-controls'
          isFullscreen={isFullscreen}
          ref={mapControlsRef}
        >
          <ZoomOutIcon
            role='button'
            className={!isZoomedIn ? 'disabled' : undefined}
            onClick={() => zoomOut()}
          />
          <ZoomInIcon
            role='button'
            className={zoomLevel >= MAX_ZOOM ? 'disabled' : undefined}
            onClick={() => {
              setIsZoomedIn(true)
              activeMunicipalityId
                ? zoomToActiveMunicipality()
                : zoomInToWhereIam()
              // zoomToActiveMunicipality()
            }}
          />
          <FullscreenButtonWrapper>
            {controlsAreVisible && fullscreenHint && (
              <Hint>{fullscreenHint}</Hint>
            )}
            {isFullscreen ? (
              <ExitFullScreenIcon onClick={fullscreenButtonPressed} />
            ) : (
              <FullScreenIcon onClick={fullscreenButtonPressed} />
            )}
          </FullscreenButtonWrapper>
        </MapControls>
      </div>
    </div>
  )
}
const Hint = styled.div`
  line-height: 2;
  position: absolute;
  color: var(--text-color);
  right: 12px;
  top: 0;
  transform: translate(50%, -150%);
  background: var(--hint-background);
  border-radius: 0.5em;
  padding: 0 0.9em;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  font-size: 0.6em;
  text-align: center;
  z-index: 100;
  transition: opacity 0.2s ease-in-out;
  pointer-events: none;
  // prevent line breaks
  white-space: nowrap;
  opacity: 0;
  animation: riseUp 5s ease-in-out;
  @keyframes riseUp {
    10% {
      opacity: 0;
      transform: translateX(50%) translateY(1em);
    }
    15% {
      opacity: 1;
      transform: translateX(50%) translateY(-150%);
    }
    95% {
      opacity: 1;
      transform: translateX(50%) translateY(-150%);
    }
    100% {
      opacity: 0;
      transform: translateX(50%) translateY(1em);
    }
  }

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 0.5em solid transparent;
    border-top-color: var(--hint-background);
  }
`
const MapControls = styled.div<{ isFullscreen?: boolean }>`
  --brandblue-main: #202346;
  --gray-4: #d4d4d4;
  --gray-5: #e9e9e9;
  display: flex;
  justify-content: flex-end;
  margin-right: ${({ isFullscreen }) => (isFullscreen ? '1.5em' : '1em')};
  padding-bottom: ${({ isFullscreen }) => (isFullscreen ? '1.5em' : '0em')};
  transition: padding-bottom 0.3s ease-in-out;
  pointer-events: none;
  /* transform: translateY(-100%); */
  color: var(--brandblue-main);
  position: sticky;
  z-index: 101;
  gap: 0.5em;
  bottom: 0;
  svg {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    @media (min-width: 535px) {
      &:hover {
        background-color: var(--gray-5);
      }
    }
    @media (max-width: 535px) {
      width: 1.5em;
      height: 1.5em;
    }
    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
    transition: all 0.3s ease-in-out;

    /* transform: translate(0, -100%); */
    pointer-events: all;
    /* position: absolute; */
    cursor: pointer;
    background-color: var(--gray-4);
    border-radius: 10%;
  }
`
const FullscreenButtonWrapper = styled.span`
  line-height: 0;
  @media (min-width: 1200px) {
    display: none;
  }
`
export default React.memo(
  ChoroplethMap,
  (prevProps, nextProps) =>
    prevProps.municipalityData === nextProps.municipalityData &&
    prevProps.activeMunicipalityId === nextProps.activeMunicipalityId &&
    prevProps.minValue === nextProps.minValue &&
    prevProps.maxValue === nextProps.maxValue &&
    prevProps.year === nextProps.year &&
    prevProps.indexOfPropertyToCompare === nextProps.indexOfPropertyToCompare &&
    prevProps.propertyNameToCompare === nextProps.propertyNameToCompare &&
    prevProps.isFullscreen === nextProps.isFullscreen
  // && prevProps.newActiveMunicipalityIdHandler === nextProps.newActiveMunicipalityIdHandler
)
